import React, { useCallback, useEffect, useState } from 'react';
import { Grid, Row, Col, Panel, Button, Modal, Alert, FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import dateFormat from 'dateformat';
import { fetchApi } from '../../Network/NetworkService';

const THRESHOLD_DAYS = 4;

const formatDate = (value) => (value ? dateFormat(new Date(value), 'dd/mm/yyyy HH:MM') : '');
const formatDateOnly = (ymd) => (ymd ? dateFormat(new Date(`${ymd}T00:00:00`), 'dd/mm/yyyy') : '');

// Convert a "YYYY-MM-DD" picker value to an ISO string. `from` is the local
// start of day (inclusive); `to` is the local start of the *next* day, paired
// with the server's `$lt` to give an exclusive end-of-day upper bound that
// avoids edge cases at 23:59:59.999.
const fromDateToIso = (ymd) => {
    if (!ymd) return '';
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
};
const toDateToIso = (ymd) => {
    if (!ymd) return '';
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d + 1, 0, 0, 0, 0).toISOString();
};

// "YYYY-MM-DD" of (today - THRESHOLD_DAYS) in local time.
const computeMaxDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - THRESHOLD_DAYS);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const CancelStaleWaiting = () => {
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [maxDate, setMaxDate] = useState(computeMaxDate());

    useEffect(() => {
        // Recompute the max bound every minute so a long-open page stays accurate.
        const id = setInterval(() => setMaxDate(computeMaxDate()), 60 * 1000);
        return () => clearInterval(id);
    }, []);

    const clamp = (value) => (value && value > maxDate ? maxDate : value);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState([]);
    const [stale, setStale] = useState(false);

    const fetchPreview = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (from) params.set('from', fromDateToIso(from));
            if (to) params.set('to', toDateToIso(to));
            const qs = params.toString();
            const res = await fetchApi(`/requests/stale-waiting/count${qs ? `?${qs}` : ''}`, { method: 'GET' });
            setPreview({
                count: res.count,
                cutoff: res.cutoff,
                effectiveTo: res.effectiveTo,
                thresholdDays: res.thresholdDays,
            });
            setStale(false);
        } catch (e) {
            setError(typeof e === 'string' ? e : 'Erreur lors du chargement.');
        } finally {
            setLoading(false);
        }
    }, [from, to]);

    // Initial fetch only — subsequent runs require the user to click "Recalculer".
    useEffect(() => { fetchPreview(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const handleConfirm = async () => {
        setCancelling(true);
        setError(null);
        try {
            const res = await fetchApi('/requests/stale-waiting/cancel', {
                method: 'POST',
                body: JSON.stringify({
                    from: from ? fromDateToIso(from) : undefined,
                    to: to ? toDateToIso(to) : undefined,
                }),
            });
            const previewedCount = preview ? preview.count : null;
            const drifted = previewedCount != null && res.modifiedCount !== previewedCount;
            const entry = {
                modifiedCount: res.modifiedCount,
                previewedCount,
                drifted,
                from: from ? fromDateToIso(from) : null,
                effectiveTo: res.effectiveTo,
                canceledAt: res.canceledAt,
            };
            setResult(entry);
            setHistory((prev) => [entry, ...prev].slice(0, 10));
            setShowConfirm(false);
            fetchPreview();
        } catch (e) {
            setError(typeof e === 'string' ? e : 'Erreur lors de l\'annulation.');
        } finally {
            setCancelling(false);
        }
    };

    const thresholdDays = preview ? preview.thresholdDays : 4;
    const cutoffLabel = formatDate(preview && preview.cutoff);
    const effectiveToLabel = formatDate(preview && preview.effectiveTo);
    const cappedByCutoff = preview && to && preview.effectiveTo &&
        new Date(preview.effectiveTo).getTime() < new Date(toDateToIso(to)).getTime();

    return (
        <Grid fluid style={{ marginTop: 20 }}>
            <Row>
                <Col xs={12}>
                    <Panel>
                        <Panel.Heading>
                            <Panel.Title>Annuler les requêtes en attente anciennes</Panel.Title>
                        </Panel.Heading>
                        <Panel.Body>
                            <p>
                                Cette action passe en <strong>canceled</strong> toutes les requêtes
                                en état <strong>waiting</strong> dont <code>createdAt</code> est dans
                                la période sélectionnée.
                            </p>
                            <p style={{ color: '#777' }}>
                                Quoi qu'il arrive, les requêtes créées il y a moins de{' '}
                                <strong>{thresholdDays} jours</strong> ne sont jamais touchées
                                (borne haute plafonnée à <strong>{cutoffLabel}</strong>).
                            </p>

                            <Row>
                                <Col sm={4}>
                                    <FormGroup>
                                        <ControlLabel>Du</ControlLabel>
                                        <FormControl
                                            type="date"
                                            value={from}
                                            max={maxDate}
                                            onChange={(e) => { setFrom(clamp(e.target.value)); setStale(true); }}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col sm={4}>
                                    <FormGroup>
                                        <ControlLabel>Au</ControlLabel>
                                        <FormControl
                                            type="date"
                                            value={to}
                                            max={maxDate}
                                            onChange={(e) => { setTo(clamp(e.target.value)); setStale(true); }}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col sm={4} style={{ paddingTop: 25 }}>
                                    <Button
                                        bsStyle={stale ? 'primary' : 'default'}
                                        onClick={fetchPreview}
                                        disabled={loading}
                                    >
                                        {loading ? 'Chargement…' : 'Recalculer'}
                                    </Button>
                                    {(from || to) && (
                                        <Button
                                            bsStyle="link"
                                            onClick={() => { setFrom(''); setTo(''); setStale(true); }}
                                        >
                                            Réinitialiser
                                        </Button>
                                    )}
                                </Col>
                            </Row>

                            {cappedByCutoff && (
                                <Alert bsStyle="info">
                                    La borne haute saisie est dans la zone protégée. Elle a été
                                    plafonnée à <strong>{effectiveToLabel}</strong>.
                                </Alert>
                            )}

                            {error && <Alert bsStyle="danger">{error}</Alert>}
                            {result && (
                                <Alert bsStyle={result.drifted ? 'warning' : 'success'}>
                                    {result.modifiedCount} requête(s) annulée(s).
                                    {result.drifted && (
                                        <> {' '}(prévision : {result.previewedCount} — un changement
                                        a eu lieu entre l'aperçu et l'action)</>
                                    )}
                                </Alert>
                            )}

                            <div style={{ marginTop: 20 }}>
                                {preview && (
                                    <div>
                                        <div style={{ marginBottom: 10 }}>
                                            <strong>{preview.count}</strong> requête(s) éligible(s)
                                            {from && <> à partir du <strong>{formatDateOnly(from)}</strong></>}
                                            {' '}jusqu'au <strong>{effectiveToLabel}</strong>.
                                            {stale && (
                                                <span style={{ marginLeft: 10, color: '#a94442' }}>
                                                    (période modifiée — cliquez sur Recalculer)
                                                </span>
                                            )}
                                        </div>
                                        <Button
                                            bsStyle="danger"
                                            disabled={preview.count === 0 || loading || stale}
                                            onClick={() => setShowConfirm(true)}
                                        >
                                            Annuler ces requêtes
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </Panel.Body>
                    </Panel>
                </Col>
            </Row>

            {history.length > 0 && (
                <Row>
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading>
                                <Panel.Title>Historique de cette session</Panel.Title>
                            </Panel.Heading>
                            <Panel.Body>
                                <table className="table table-condensed">
                                    <thead>
                                        <tr>
                                            <th>Quand</th>
                                            <th>Période ciblée</th>
                                            <th style={{ textAlign: 'right' }}>Annulées</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map((h, i) => (
                                            <tr key={i}>
                                                <td>{formatDate(h.canceledAt)}</td>
                                                <td>
                                                    {h.from ? formatDate(h.from) : '—'}
                                                    {' → '}
                                                    {formatDate(h.effectiveTo)}
                                                </td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <strong>{h.modifiedCount}</strong>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <p style={{ color: '#777', fontSize: 12, marginBottom: 0 }}>
                                    Les requêtes annulées sont marquées avec{' '}
                                    <code>cancelReason: "admin-stale-cleanup"</code>,{' '}
                                    <code>canceledBy</code> et <code>canceledAt</code> en base.
                                </p>
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            <Modal show={showConfirm} onHide={() => !cancelling && setShowConfirm(false)}>
                <Modal.Header closeButton={!cancelling}>
                    <Modal.Title>Confirmer l'annulation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {preview && (
                        <p>
                            Vous êtes sur le point d'annuler <strong>{preview.count}</strong> requête(s)
                            en état <strong>waiting</strong>
                            {from && <> créées à partir du <strong>{formatDateOnly(from)}</strong></>}
                            {' '}et avant le <strong>{effectiveToLabel}</strong>.
                        </p>
                    )}
                    <p style={{ color: '#a94442' }}>
                        Cette action est irréversible.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setShowConfirm(false)} disabled={cancelling}>
                        Annuler
                    </Button>
                    <Button bsStyle="danger" onClick={handleConfirm} disabled={cancelling}>
                        {cancelling ? 'Annulation en cours…' : 'Confirmer'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Grid>
    );
};

export default CancelStaleWaiting;
